import type { CloudAllowanceStatus, CloudWorkspace } from "@afar-research/contracts";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { getClientSettingsSnapshot, hydrateClientSettings } from "../clientSettingsStore";
import { addedConnectEnvironments, connectAccountKey } from "../connection/connectEnvironments";
import { checkConnectSnapshot, getConnectSnapshot } from "../connection/connectSnapshot";
import type { ConnectSnapshot } from "../connection/connectSnapshot.logic";
import {
  type AccountEntry,
  type AccountRead,
  resolveAccountEntry,
} from "../environments/primary/accountEntry.logic";
import { readLocalApi } from "../localApi";
import { invitationTokenFromSearch } from "./settings/afarNetworkSettings.logic";
import { CloudWorkspacesPanel, cloudBridge } from "./settings/CloudWorkspaces";
import {
  type CloudUsageBlocker,
  describeCloudUsageLeft,
  describePlanIncludes,
} from "./settings/CloudWorkspaces.logic";
import { Button } from "./ui/button";
import { toastManager } from "./ui/toast";

interface AccountView {
  readonly entry: AccountEntry;
  readonly email: string | null;
  readonly allowance: CloudAllowanceStatus | null;
  readonly workspaces: ReadonlyArray<CloudWorkspace>;
}

// The choice to add one's own machine instead of a Cloud Workspace lasts the session, so the app
// stays open while the person is in Settings → Network adding it, through re-checks and reloads.
const ADDING_OWN_MACHINE_KEY = "afar:entry:adding-own-machine";
const readAddingOwnMachine = () => {
  try {
    return window.sessionStorage.getItem(ADDING_OWN_MACHINE_KEY) === "1";
  } catch {
    return false;
  }
};
const rememberAddingOwnMachine = () => {
  try {
    window.sessionStorage.setItem(ADDING_OWN_MACHINE_KEY, "1");
  } catch {
    // Without session storage the choice lasts until the next check.
  }
};

const failedView: AccountView = {
  entry: resolveAccountEntry({ _tag: "failed" }),
  email: null,
  allowance: null,
  workspaces: [],
};

/**
 * The account's entry, from what this browser last heard from Connect: at once when it holds an
 * answer for the signed-in account, then from Connect itself (`fromConnect`), asked once unless the
 * snapshot is still fresh or `force` asks regardless.
 */
async function readAccount(
  options: { readonly force: boolean },
  show: (view: AccountView) => void,
): Promise<void> {
  const bridge = readLocalApi()?.connect;
  try {
    if (!bridge?.cloudAllowance || !bridge.cloudWorkspaces) throw new Error("no Connect bridge");
    const status = await bridge.getStatus();
    if (status.state !== "signed-in") throw new Error("signed out");
    await hydrateClientSettings();
    const accountKey = connectAccountKey(status.activeAccountId);
    const email = status.accounts?.find((entry) => entry.id === status.activeAccountId)?.email;
    const view = (snapshot: ConnectSnapshot): AccountView => {
      // The plan decides entry; a snapshot without it is no answer.
      if (snapshot.allowance === null) return failedView;
      const read: AccountRead = {
        _tag: "read",
        allowance: snapshot.allowance,
        workspaces: snapshot.workspaces,
        added: addedConnectEnvironments(getClientSettingsSnapshot(), accountKey),
        networkMachines: (bridge.networks?.rememberedMachines() ?? []).map((machine) => machine.id),
        addingOwnMachine: readAddingOwnMachine(),
        openingInvitation: invitationTokenFromSearch(window.location.search) !== null,
      };
      return {
        entry: resolveAccountEntry(read),
        email: email ?? null,
        allowance: snapshot.allowance,
        workspaces: snapshot.workspaces,
      };
    };
    const stored = getConnectSnapshot();
    if (!options.force && stored?.accountKey === accountKey) show(view(stored));
    const checked = await checkConnectSnapshot({ force: options.force });
    show(checked === null ? failedView : view(checked));
  } catch {
    show(failedView);
  }
}

/**
 * Stands in for the Runtime Web Client's app until the signed-in account can use it: the Plan
 * Gate, then First Workspace Setup. Both are checked on entry and while they show, never once the
 * app is open, so running out of usage mid-session leaves the user where they are.
 */
export function AccountEntryGate({ children }: { readonly children: ReactNode }) {
  const [view, setView] = useState<AccountView | null>(null);
  const opened = useRef(false);
  const generation = useRef(0);
  const check = useCallback(async (force = false) => {
    if (opened.current) return;
    const current = ++generation.current;
    await readAccount({ force }, (next) => {
      if (opened.current || current !== generation.current) return;
      if (next.entry.kind === "app") opened.current = true;
      setView(next);
    });
  }, []);
  useEffect(() => {
    void check();
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [check]);
  const open = useCallback(() => {
    opened.current = true;
    setView((current) => current && { ...current, entry: { kind: "app" } });
  }, []);
  const navigate = useNavigate();
  // Your own machines are a way in that needs no Cloud Workspace (ADR-0146): they are added from
  // Settings → Network, which only the open app can show.
  const addOwnMachine = useCallback(() => {
    rememberAddingOwnMachine();
    open();
    void navigate({ to: "/settings/network" });
  }, [navigate, open]);
  // A Cloud Workspace is rented on the Network page too, with every other machine.
  const rentWorkspace = useCallback(() => {
    rememberAddingOwnMachine();
    open();
    void navigate({ to: "/settings/network", search: { add: "rent" } });
  }, [navigate, open]);

  if (view === null) return <EntryFrame title="Checking your account…" />;
  switch (view.entry.kind) {
    case "app":
      return children;
    case "unreadable":
      return (
        <EntryFrame
          title="AFAR Connect could not be reached"
          // Reloading enters again from sign-in, so an account that signed out meanwhile is asked to
          // sign in rather than retrying a read that cannot succeed.
          actions={<Button onClick={() => window.location.reload()}>Retry</Button>}
        >
          <p className="text-muted-foreground text-sm">
            Your account&apos;s plan could not be checked. Check your connection and try again.
          </p>
        </EntryFrame>
      );
    case "plan-gate":
      return <PlanGate view={view} reason={view.entry.reason} onCheck={() => void check(true)} />;
    case "first-workspace-setup": {
      const bridge = cloudBridge(readLocalApi()?.connect);
      return (
        <EntryFrame
          title="Set up your Cloud Workspace"
          wide
          actions={
            readLocalApi()?.connect?.networks ? (
              <Button variant="outline" onClick={addOwnMachine}>
                Add your own machine instead
              </Button>
            ) : undefined
          }
        >
          <p className="text-muted-foreground text-sm">
            {view.workspaces.length === 0
              ? "Rent your first Cloud Workspace to start working in AFAR."
              : "Connect one of your Cloud Workspaces to use it in this browser."}
          </p>
          {bridge && (
            <CloudWorkspacesPanel
              listConnectable
              bridge={bridge}
              offline={false}
              onAdded={open}
              onFailed={() => void check(true)}
              onRent={rentWorkspace}
            />
          )}
        </EntryFrame>
      );
    }
  }
}

function PlanGate({
  view,
  reason,
  onCheck,
}: {
  readonly view: AccountView;
  readonly reason: CloudUsageBlocker;
  readonly onCheck: () => void;
}) {
  const allowance = view.allowance;
  const current = allowance?.subscription?.plan ?? null;
  const deletions = view.workspaces.flatMap((workspace) =>
    workspace.deleteAfter === null ? [] : [workspace.deleteAfter],
  );
  const deleteAfter = deletions.length > 0 ? Math.min(...deletions) : undefined;
  const account = view.email ?? "your account";
  return (
    <EntryFrame
      title={
        reason === "no-plan"
          ? "A plan is needed to use AFAR"
          : ((allowance && describeCloudUsageLeft(allowance)) ?? "The usage is used up.")
      }
      actions={<Button onClick={onCheck}>Check again</Button>}
    >
      <p className="text-muted-foreground text-sm">
        {reason === "no-plan"
          ? `Ask an administrator to attach a plan to ${account}.`
          : `Ask an administrator to add usage or change the plan for ${account}.`}
      </p>
      {deleteAfter !== undefined && (
        <p className="text-destructive text-sm">
          Your Cloud Workspaces will be permanently deleted after{" "}
          {new Date(deleteAfter).toLocaleString()} unless usage is added before then.
        </p>
      )}
      {allowance && allowance.plans.length > 0 && (
        <ul className="space-y-2">
          {allowance.plans.map((plan) => (
            <li key={plan.id} className="rounded border p-3">
              <p className="font-medium text-sm">
                {plan.label}
                {plan.id === current ? (
                  <span className="ml-2 text-muted-foreground text-xs">Current plan</span>
                ) : null}
              </p>
              <p className="text-muted-foreground text-xs">{describePlanIncludes(plan)}</p>
            </li>
          ))}
        </ul>
      )}
    </EntryFrame>
  );
}

function EntryFrame({
  title,
  wide = false,
  actions,
  children,
}: {
  readonly title: string;
  readonly wide?: boolean;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  const bridge = readLocalApi()?.connect;
  return (
    <main className="flex min-h-screen justify-center overflow-y-auto bg-background p-6 text-foreground">
      <div className={`my-auto flex w-full flex-col gap-4 ${wide ? "max-w-xl" : "max-w-md"}`}>
        <h1 className="font-semibold text-lg">{title}</h1>
        {children}
        {(actions || bridge) && (
          <div className="flex gap-2">
            {actions}
            {bridge && (
              <Button
                variant="ghost"
                // Like Retry, signing out enters again from sign-in: nothing on this screen
                // follows the account once it is gone.
                onClick={() =>
                  void bridge.signOut().then(
                    () => window.location.reload(),
                    (error: unknown) =>
                      toastManager.add({
                        type: "error",
                        title: "Could not sign out",
                        description: error instanceof Error ? error.message : String(error),
                      }),
                  )
                }
              >
                Sign out
              </Button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
